// period-label — shared SSOT for "what window does this widget show" labels.
//
// Every dashboard badge/title/subtitle that names the selected PeriodBar
// window must go through one of these, so a widget never claims a window
// ("This Week", "Last 7 Days") that differs from what it actually queried
// (WS6-T3). Consolidates what used to be three separate, drifting
// implementations: a private `getResetLabel` in usePeriodNavigation.ts, a
// local `PERIOD_DAYS` + ternary duplicated in TrendsTab and client/OverviewTab,
// and several dashboards that hardcoded "This Week"/"Last 7 Days" regardless
// of the selected period.

import type { TFunction } from 'i18next';
import type { Period } from '@/components/layout/PeriodBar';

/** Chart-window day count per period. */
const PERIOD_DAYS: Record<Period, number> = {
  today: 1,
  day: 1,
  week: 7,
  month: 30,
  quarter: 90,
};

/**
 * Calendar-style "current window" label for badges/subtitles: "Today" /
 * "This Week" / "This Month" / "This Quarter". Reuses the same
 * `common:periodNav.*` keys as the PeriodBar reset button
 * (usePeriodNavigation.ts) so a widget's badge and the navigation control
 * never disagree about what "this period" is called.
 */
export function periodWindowLabel(period: Period, t: TFunction): string {
  switch (period) {
    case 'day':
    case 'today':
      return t('common:periodNav.today', 'Today');
    case 'week':
      return t('common:periodNav.thisWeek', 'This week');
    case 'month':
      return t('common:periodNav.thisMonth', 'This month');
    case 'quarter':
      return t('common:periodNav.thisQuarter', 'This quarter');
  }
}

/**
 * The window immediately before the selected period: "Yesterday" / "Last
 * Week" / "Last Month" / "Last Quarter". Pairs with periodWindowLabel for
 * "this vs last" comparison widgets (e.g. MyProgressTab's monthly card).
 */
export function prevPeriodWindowLabel(period: Period, t: TFunction): string {
  switch (period) {
    case 'day':
    case 'today':
      return t('common:common.yesterday', 'Yesterday');
    case 'week':
      return t('common:periodNav.lastWeek', 'Last week');
    case 'month':
      return t('common:periodNav.lastMonth', 'Last month');
    case 'quarter':
      return t('common:periodNav.lastQuarter', 'Last quarter');
  }
}

/**
 * Numeric chart/table-header window label: "Today" / "Last 7 Days" / "Last
 * 30 Days" / "Last 90 Days". SSOT for chart titles that describe the window
 * as a day count rather than calendar language (was duplicated as a local
 * PERIOD_DAYS + ternary in TrendsTab and client/OverviewTab, and missing
 * entirely from AgentDashboard/admin OverviewTab's hardcoded "Records Last 7
 * Days").
 */
export function chartWindowLabel(period: Period, t: TFunction): string {
  if (period === 'today' || period === 'day') {
    return t('common:periodNav.today', 'Today');
  }
  return t('common:periodNav.lastNDays', 'Last {{n}} days', {
    n: PERIOD_DAYS[period],
  });
}
